import type { DocumentNode } from "graphql";
import { OperationTypeNode } from "graphql";
import type { Client, CombinedError } from "@urql/core";
import { pipe, subscribe } from "wonka";
import { createClient, dedupExchange, fetchExchange, makeOperation, subscriptionExchange } from "@urql/core";
import { authExchange } from "@urql/exchange-auth";
import { createClient as createWSClient } from "graphql-ws";
import type { Subscriber } from "rxjs";
import { Observable } from "rxjs";
import { put } from "redux-saga/effects";

import type { Requester } from "../generated/graphql";
import { getSdk } from "../generated/graphql";

import type { RootState } from "./rootReducer";
import type { AppDispatch } from "./store";

let CLIENT: Client;

function calculateExponentialBackoff(count: number, limit = 30000) {
  return Math.min(0.5 * 1.5 ** count - 0.5, limit / 1000) * 1000;
}

// See https://github.com/FormidableLabs/urql/tree/main/exchanges/auth#quick-start-guide
const getGqlClient = (dispatch: AppDispatch, getState: () => RootState, authTokenOverride?: string) => {
  if (!CLIENT || authTokenOverride) {
    const getAuthToken = () => authTokenOverride || getState().authV2.token?.access_token;

    const wsClient = createWSClient({
      // http:// => ws://
      // https:// => wss://
      url: `${getState().config.apiUrl.replace(/http(s?):\/\//, "ws$1://")}/graphql`,
      connectionParams: () => {
        const auth = getState().authV2;
        const token = getAuthToken();
        if (!(auth.logged_in && token)) {
          // the token isn't in the auth state, return the operation without changes
          console.warn("Connecting to websocket without auth token, this will probably fail");
          return {};
        }
        return {
          authorization: token,
        };
      },
      retryAttempts: 999,
      retryWait: (retries) =>
        new Promise((res) => {
          setTimeout(res, calculateExponentialBackoff(retries));
        }),
      isFatalConnectionProblem: () => false,
    });

    CLIENT = createClient({
      url: `${getState().config.apiUrl}/graphql`,
      exchanges: [
        dedupExchange,
        authExchange<RootState["authV2"]>({
          addAuthToOperation: ({ operation }) => {
            // the token isn't in the auth state, return the operation without changes
            const token = getAuthToken();
            if (!token) {
              console.warn("Making graphql request without auth, this will probably fail");
              return operation;
            }

            const fetchOptions =
              typeof operation.context.fetchOptions === "function"
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {};

            return makeOperation(
              operation.kind,
              operation as any,
              {
                ...operation.context,
                fetchOptions: {
                  ...fetchOptions,
                  headers: {
                    ...fetchOptions.headers,
                    authorization: token,
                  },
                },
              } as any
            ) as any;
          },

          getAuth: async () => getState().authV2,
          willAuthError: ({ authState }) => {
            if (!authState?.logged_in) return true;
            // e.g. check for expiration, existence of auth etc
            return false;
          },
          didAuthError: ({ error }) =>
            // check if the error was an auth error (this can be implemented in various ways, e.g. 401 or a special error code)
            error.graphQLErrors.some((e) => e.extensions?.code === "FORBIDDEN"),
        }) as any,
        fetchExchange,
        subscriptionExchange({
          forwardSubscription: (operation) => ({
            subscribe: (sink) => ({
              unsubscribe: wsClient.subscribe(operation, sink),
            }),
          }),
        }),
      ],
    });
  }
  return CLIENT;
};

const validDocDefOps = ["mutation", "query", "subscription"];

function instantiateSdk(client: Client) {
  const requester = <R, V extends Record<string, unknown>>(doc: DocumentNode, vars?: V): Promise<R> | Observable<R> => {
    // Valid document should contain *single* query or mutation unless it's has a fragment
    if (
      doc.definitions.filter((d) => d.kind === "OperationDefinition" && validDocDefOps.includes(d.operation)).length !==
      1
    ) {
      throw new Error("GraphQL Document must define a single query, mutation, or subscription");
    }

    const definition = doc.definitions[0];

    // Valid document should contain *OperationDefinition*
    if (definition.kind !== "OperationDefinition") {
      throw new Error(`GraphQL Document must define an operation, not ${definition.kind}`);
    }

    // We are saving the stack here because when reporting any failure from this code, we might
    // as well report location of the originating network request whereas if we didn't do this
    // the call stack we would get would contain only network/websocket callbacks and nothing useful
    const callerStack = new Error().stack;

    const unpackCombinedError = (combined: CombinedError) => {
      // There's a typing bug in 'CombinedError.networkError'. They defined it as a
      // single object whereas it's actually an array, so we do this magic on next line
      const networkErrors = combined.networkError as unknown as Error[] | undefined;

      // CombinedError type can have an array of N network errors and M graphql errors
      // all packed into a single "combined" error. Someday, there might be a reason to
      // return ALL those errors so that the caller would handle them, somehow. For now
      // we are going to pick ONE inner error and return that one.
      const innerErr: Error & { extensions?: any } =
        networkErrors && networkErrors.length > 0
          ? networkErrors[0]
          : combined.graphQLErrors && combined.graphQLErrors.length > 0
          ? combined.graphQLErrors[0]
          : combined;

      const errOut = new Error();
      Object.assign(errOut, innerErr);
      errOut.stack = callerStack;
      errOut.name = innerErr?.extensions?.response?.error || innerErr.name;
      errOut.message = innerErr?.extensions?.response?.message || innerErr.message;
      return errOut;
    };

    switch (definition.operation) {
      case OperationTypeNode.MUTATION: {
        return (async () => {
          const response = await client.mutation<R, V>(doc, vars).toPromise();
          if (response.error) {
            throw unpackCombinedError(response.error);
          }
          return response.data as R;
        })();
      }
      case OperationTypeNode.QUERY: {
        return (async () => {
          const response = await client.query<R, V>(doc, vars).toPromise();
          if (response.error) {
            throw unpackCombinedError(response.error);
          }
          return response.data as R;
        })();
      }
      case OperationTypeNode.SUBSCRIPTION: {
        return new Observable((observer: Subscriber<R>) => {
          const subscription = pipe(
            client.subscription<R, V>(doc, vars),
            subscribe((res) => {
              if (res.error) {
                observer.error(unpackCombinedError(res.error));
              } else if (res.data) {
                observer.next(res.data);
              }
            })
          );

          return subscription.unsubscribe.bind(subscription);
        });
      }
      default: {
        throw new Error("Something went wrong");
      }
    }
  };

  return getSdk(requester as Requester);
}

export const getTheirStorySdkWrapped = (authToken?: string) => (dispatch: AppDispatch, getState: () => RootState) =>
  instantiateSdk(getGqlClient(dispatch, getState, authToken));

export function getTheirStorySdk(dispatch: AppDispatch, getState: () => RootState) {
  return getTheirStorySdkWrapped()(dispatch, getState);
}

export type TheirStorySdk = ReturnType<typeof getTheirStorySdk>;

/*
Explanation of what's going on here:
  * `getTheirStorySdk` has the following signature: `(dispatch: AppDispatch, getState: () => RootState)`
  * Redux's `dispatch` can take a function with that signature, which is how thunks work
  * `dispatch` and `getState` aren't available directly in sagas, so you can't easily pass them to `getTheirStorySdk`
  * `put` effectively calls Redux's `dispatch`
  * `put`'s Typescript type doesn't know about all of the variations that `dispatch` does, so we have to teach it
*/
export function* getTheirStorySdkSaga(authTokenOverride?: string) {
  const sdk: TheirStorySdk = yield put(getTheirStorySdkWrapped(authTokenOverride) as any);
  return sdk;
}
